import { z } from "zod";
import {
  type CustomAllowance,
  type CustomForm,
  type CustomItem,
  type CustomOption,
  type CustomSection,
  type CustomUnitPrice,
  customFormSchema,
  customTotals,
  newPartId,
  optionLabel,
} from "./custom-proposal";
import { dollarsToCents } from "./proposals/money";

/**
 * The AI that builds a custom proposal with the rep. It changes the proposal only through these
 * operations, so nothing it says changes anything unless it shows up in the draft. It speaks
 * dollars, plain quantities and percents; the conversion to cents, thousandths and the totals are
 * done here and in custom-proposal.ts, never by the AI.
 *
 * Every price it sets says where it came from (`priceSource`): "rep" when the rep gave or
 * confirmed the number, otherwise the basis ("NMT's 3 past bathroom remodels", "sub bid $18,400 +
 * 15% markup", "general market estimate for central Oklahoma"). Anything not from the rep is an
 * AI estimate the rep has to accept before the proposal can be sent.
 */

const money = z.number().finite().min(0).max(10_000_000);
const quantity = z.number().finite().min(0).max(1_000_000);
const percent = z.number().finite().min(0).max(100);
const source = z.string().trim().min(1).max(300).describe('Where the price came from: "rep" if the rep gave or confirmed it, else its basis.');
const ref = z.string().trim().min(1).max(40);
const text = (n: number) => z.string().max(n);
const strings = z.array(z.string().max(1000)).max(100);

const itemIn = z.object({ description: text(500), quantity, unit: text(20).default("EA"), unitPrice: money, unitCost: money.nullable().optional(), priceSource: source });

const ops = [
  z.object({
    op: z.literal("set_proposal"),
    title: text(120).optional(),
    intro: text(10_000).optional(),
    market: z.enum(["residential", "commercial"]).optional(),
    cover: z.object({ projectName: text(200), location: text(300), bidDate: text(60), toCompany: text(200), toContact: text(200), basedOn: text(500) }).partial().optional(),
    warranty: text(4000).optional(),
    notes: text(4000).optional(),
    validDays: z.number().int().min(1).max(120).optional(),
    discount: money.optional(),
    taxPercent: z.number().finite().min(0).max(20).optional(),
  }),
  z.object({
    op: z.literal("add_section"),
    heading: text(200),
    description: text(10_000).optional(),
    included: strings.optional(),
    pricing: z.enum(["lump", "items", "included"]),
    price: money.optional(),
    cost: money.nullable().optional(),
    priceSource: source.optional(),
    items: z.array(itemIn).max(100).optional(),
    position: z.number().int().min(1).optional().describe("1 = first; default last."),
  }),
  z.object({
    op: z.literal("update_section"),
    section: ref,
    heading: text(200).optional(),
    description: text(10_000).optional(),
    included: strings.optional(),
    pricing: z.enum(["lump", "items", "included"]).optional(),
    price: money.optional(),
    cost: money.nullable().optional(),
    priceSource: source.optional(),
  }),
  z.object({ op: z.literal("remove_section"), section: ref }),
  z.object({ op: z.literal("move_section"), section: ref, position: z.number().int().min(1) }),
  z.object({ op: z.literal("add_item"), section: ref, ...itemIn.shape }),
  z.object({ op: z.literal("update_item"), section: ref, item: ref, description: text(500).optional(), quantity: quantity.optional(), unit: text(20).optional(), unitPrice: money.optional(), unitCost: money.nullable().optional(), priceSource: source.optional() }),
  z.object({ op: z.literal("remove_item"), section: ref, item: ref }),
  z.object({
    op: z.literal("add_allowance"),
    description: text(300),
    quantity: quantity.optional(),
    unit: text(20).optional(),
    unitPrice: money.optional(),
    amount: money.optional().describe("A set amount, when not per unit."),
    cost: money.nullable().optional(),
    priceSource: source,
  }),
  z.object({ op: z.literal("update_allowance"), allowance: ref, description: text(300).optional(), quantity: quantity.nullable().optional(), unit: text(20).optional(), unitPrice: money.nullable().optional(), amount: money.optional(), cost: money.nullable().optional(), priceSource: source.optional() }),
  z.object({ op: z.literal("remove_allowance"), allowance: ref }),
  z.object({ op: z.literal("add_option"), kind: z.enum(["add", "deduct"]), label: text(60).optional(), description: text(1000), amount: money, cost: money.nullable().optional(), priceSource: source }),
  z.object({ op: z.literal("update_option"), option: ref, kind: z.enum(["add", "deduct"]).optional(), label: text(60).optional(), description: text(1000).optional(), amount: money.optional(), cost: money.nullable().optional(), priceSource: source.optional() }),
  z.object({ op: z.literal("remove_option"), option: ref }),
  z.object({ op: z.literal("add_unit_price"), description: text(300), unit: text(20), unitPrice: money, priceSource: source }),
  z.object({ op: z.literal("update_unit_price"), unitPrice: ref, description: text(300).optional(), unit: text(20).optional(), price: money.optional(), priceSource: source.optional() }),
  z.object({ op: z.literal("remove_unit_price"), unitPrice: ref }),
  z.object({ op: z.literal("set_list"), list: z.enum(["exclusions", "qualifications", "selections"]), items: strings }),
  z.object({ op: z.literal("set_schedule"), start: text(200).optional(), duration: text(200).optional(), notes: text(2000).optional() }),
  z.object({
    op: z.literal("set_payments"),
    mode: z.enum(["milestones", "progress"]).optional(),
    milestones: z
      .array(z.object({ label: text(120), when: text(300).optional(), percent: percent.optional(), amount: money.optional() }))
      .max(20)
      .optional()
      .describe("In order. Each but the last needs a percent or an amount; the last is always the rest."),
    retainagePercent: z.number().finite().min(0).max(50).optional(),
    stepDown: z.object({ atCompletePercent: z.number().int().min(1).max(99), toPercent: z.number().finite().min(0).max(50) }).nullable().optional(),
    storedMaterials: z.boolean().optional(),
    terms: text(200).optional(),
  }),
  z.object({ op: z.literal("set_show"), sectionPrices: z.boolean().optional(), itemDetail: z.boolean().optional(), allowanceAmounts: z.boolean().optional(), scheduleOfValues: z.boolean().optional() }),
] as const;

export const customOpSchema = z.discriminatedUnion("op", ops);
export type CustomOp = z.infer<typeof customOpSchema>;

export const EDIT_PROPOSAL_TOOL = "edit_proposal";
export const editProposalInputSchema = z.object({ ops: z.array(z.unknown()).min(1).max(80).describe("Changes, applied in order.") });

/** The tool's JSON schema (ops listed in full so the model knows each shape). */
export function editProposalToolSpec() {
  const op = z.toJSONSchema(customOpSchema, { io: "input", unrepresentable: "any" }) as Record<string, unknown>;
  delete op.$schema;
  return {
    name: EDIT_PROPOSAL_TOOL,
    description:
      "Change the proposal draft. Each op is applied in order; the result says what was done, what was skipped and why, and the ids of anything added. Prices are dollars, quantities plain numbers, percents plain numbers (30 = 30%). Every price needs priceSource.",
    input_schema: { type: "object", properties: { ops: { type: "array", minItems: 1, maxItems: 80, items: op, description: "Changes, applied in order." } }, required: ["ops"] },
  };
}

const cents = (d: number) => dollarsToCents(d);
const thousandths = (n: number) => Math.round(n * 1000);
const aiOf = (s: string | undefined, had: string | null): string | null => (s === undefined ? had : s.trim().toLowerCase() === "rep" ? null : s.trim().slice(0, 300));

export interface OpsResult {
  form: CustomForm;
  /** One line per op: "done: …" or "skipped: … (why)". */
  log: string[];
  changed: boolean;
}

/** Applies the AI's operations. A bad op is skipped (and said so); the rest still apply. */
export function applyCustomOps(start: CustomForm, raw: readonly unknown[]): OpsResult {
  let f: CustomForm = structuredClone(start);
  const log: string[] = [];
  const findSection = (r: string) => f.sections.find((s) => s.id === r) ?? f.sections.find((s) => s.heading.trim().toLowerCase() === r.trim().toLowerCase());
  for (const [n, input] of raw.entries()) {
    const parsed = customOpSchema.safeParse(input);
    if (!parsed.success) {
      log.push(`${n + 1}. skipped: not a valid change (${parsed.error.issues.slice(0, 2).map((i) => `${i.path.join(".")}: ${i.message}`).join("; ")})`);
      continue;
    }
    const o = parsed.data;
    const skip = (why: string) => log.push(`${n + 1}. skipped ${o.op}: ${why}`);
    const done = (what: string) => log.push(`${n + 1}. done: ${what}`);
    switch (o.op) {
      case "set_proposal": {
        if (o.title !== undefined) f.title = o.title;
        if (o.intro !== undefined) f.intro = o.intro;
        if (o.cover) f.cover = { ...f.cover, ...o.cover };
        if (o.warranty !== undefined) f.warranty = o.warranty;
        if (o.notes !== undefined) f.notes = o.notes;
        if (o.validDays !== undefined) f.validDays = o.validDays;
        if (o.discount !== undefined) f.discountCents = cents(o.discount);
        if (o.taxPercent !== undefined) f.taxPercentMilli = thousandths(o.taxPercent);
        if (o.market && o.market !== f.market) {
          if (f.marketSetBy === "rep") {
            skip(`kept it ${f.market}: the rep picked that`);
            break;
          }
          f.market = o.market;
          f.marketSetBy = "ai";
        }
        done("updated the proposal");
        break;
      }
      case "add_section": {
        const id = newPartId();
        const s: CustomSection = {
          id,
          heading: o.heading,
          description: o.description ?? "",
          included: o.included ?? [],
          pricing: o.pricing,
          priceCents: o.price !== undefined ? cents(o.price) : 0,
          costCents: o.cost != null ? cents(o.cost) : null,
          items: (o.items ?? []).map((i) => item(i)),
          aiSource: o.pricing === "lump" && o.price !== undefined ? aiOf(o.priceSource ?? "AI estimate (no source given)", null) : null,
        };
        const at = Math.min(Math.max((o.position ?? f.sections.length + 1) - 1, 0), f.sections.length);
        f.sections.splice(at, 0, s);
        done(`added section "${o.heading}" (id ${id}${s.items.length ? `; item ids ${s.items.map((i) => i.id).join(", ")}` : ""})`);
        break;
      }
      case "update_section": {
        const s = findSection(o.section);
        if (!s) {
          skip(`no section ${o.section}`);
          break;
        }
        if (o.heading !== undefined) s.heading = o.heading;
        if (o.description !== undefined) s.description = o.description;
        if (o.included !== undefined) s.included = o.included;
        if (o.pricing !== undefined) s.pricing = o.pricing;
        if (o.cost !== undefined) s.costCents = o.cost === null ? null : cents(o.cost);
        if (o.price !== undefined) {
          s.priceCents = cents(o.price);
          s.aiSource = aiOf(o.priceSource ?? "AI estimate (no source given)", s.aiSource);
        } else if (o.priceSource !== undefined) s.aiSource = aiOf(o.priceSource, s.aiSource);
        done(`updated section "${s.heading}"`);
        break;
      }
      case "remove_section": {
        const s = findSection(o.section);
        if (!s) skip(`no section ${o.section}`);
        else {
          f.sections = f.sections.filter((x) => x !== s);
          done(`removed section "${s.heading}"`);
        }
        break;
      }
      case "move_section": {
        const s = findSection(o.section);
        if (!s) {
          skip(`no section ${o.section}`);
          break;
        }
        f.sections = f.sections.filter((x) => x !== s);
        f.sections.splice(Math.min(o.position - 1, f.sections.length), 0, s);
        done(`moved "${s.heading}" to position ${Math.min(o.position, f.sections.length)}`);
        break;
      }
      case "add_item": {
        const s = findSection(o.section);
        if (!s) {
          skip(`no section ${o.section}`);
          break;
        }
        const i = item(o);
        s.items.push(i);
        if (s.pricing !== "items" && s.pricing !== "included") s.pricing = "items";
        done(`added item "${o.description}" to "${s.heading}" (id ${i.id})`);
        break;
      }
      case "update_item": {
        const s = findSection(o.section);
        const i = s?.items.find((x) => x.id === o.item);
        if (!s || !i) {
          skip(`no item ${o.item} in ${o.section}`);
          break;
        }
        if (o.description !== undefined) i.description = o.description;
        if (o.quantity !== undefined) i.quantity = thousandths(o.quantity);
        if (o.unit !== undefined) i.unit = o.unit;
        if (o.unitCost !== undefined) i.unitCostCents = o.unitCost === null ? null : cents(o.unitCost);
        if (o.unitPrice !== undefined) {
          i.unitPriceCents = cents(o.unitPrice);
          i.aiSource = aiOf(o.priceSource ?? "AI estimate (no source given)", i.aiSource);
        } else if (o.priceSource !== undefined) i.aiSource = aiOf(o.priceSource, i.aiSource);
        done(`updated item "${i.description}"`);
        break;
      }
      case "remove_item": {
        const s = findSection(o.section);
        if (!s || !s.items.some((x) => x.id === o.item)) skip(`no item ${o.item} in ${o.section}`);
        else {
          s.items = s.items.filter((x) => x.id !== o.item);
          done(`removed an item from "${s.heading}"`);
        }
        break;
      }
      case "add_allowance": {
        const perUnit = o.quantity !== undefined && o.unitPrice !== undefined;
        if (!perUnit && o.amount === undefined) {
          skip("give a quantity and unit price, or an amount");
          break;
        }
        const a: CustomAllowance = {
          id: newPartId(),
          description: o.description,
          quantity: perUnit ? thousandths(o.quantity!) : null,
          unit: o.unit ?? "",
          unitPriceCents: perUnit ? cents(o.unitPrice!) : null,
          amountCents: perUnit ? 0 : cents(o.amount!),
          costCents: o.cost != null ? cents(o.cost) : null,
          aiSource: aiOf(o.priceSource, null),
        };
        f.allowances.push(a);
        done(`added allowance "${o.description}" (id ${a.id})`);
        break;
      }
      case "update_allowance": {
        const a = f.allowances.find((x) => x.id === o.allowance);
        if (!a) {
          skip(`no allowance ${o.allowance}`);
          break;
        }
        if (o.description !== undefined) a.description = o.description;
        if (o.unit !== undefined) a.unit = o.unit;
        if (o.cost !== undefined) a.costCents = o.cost === null ? null : cents(o.cost);
        const priced = o.quantity !== undefined || o.unitPrice !== undefined || o.amount !== undefined;
        if (o.quantity !== undefined) a.quantity = o.quantity === null ? null : thousandths(o.quantity);
        if (o.unitPrice !== undefined) a.unitPriceCents = o.unitPrice === null ? null : cents(o.unitPrice);
        if (o.amount !== undefined) a.amountCents = cents(o.amount);
        if (priced) a.aiSource = aiOf(o.priceSource ?? "AI estimate (no source given)", a.aiSource);
        else if (o.priceSource !== undefined) a.aiSource = aiOf(o.priceSource, a.aiSource);
        done(`updated allowance "${a.description}"`);
        break;
      }
      case "remove_allowance":
        if (!f.allowances.some((x) => x.id === o.allowance)) skip(`no allowance ${o.allowance}`);
        else {
          f.allowances = f.allowances.filter((x) => x.id !== o.allowance);
          done("removed an allowance");
        }
        break;
      case "add_option": {
        const x: CustomOption = { id: newPartId(), kind: o.kind, label: o.label ?? "", description: o.description, amountCents: cents(o.amount), costCents: o.cost != null ? cents(o.cost) : null, aiSource: aiOf(o.priceSource, null) };
        f.options.push(x);
        done(`added ${optionLabel(x, f.options.length - 1, f.options)} "${o.description}" (id ${x.id})`);
        break;
      }
      case "update_option": {
        const x = f.options.find((y) => y.id === o.option);
        if (!x) {
          skip(`no option ${o.option}`);
          break;
        }
        if (o.kind !== undefined) x.kind = o.kind;
        if (o.label !== undefined) x.label = o.label;
        if (o.description !== undefined) x.description = o.description;
        if (o.cost !== undefined) x.costCents = o.cost === null ? null : cents(o.cost);
        if (o.amount !== undefined) {
          x.amountCents = cents(o.amount);
          x.aiSource = aiOf(o.priceSource ?? "AI estimate (no source given)", x.aiSource);
        } else if (o.priceSource !== undefined) x.aiSource = aiOf(o.priceSource, x.aiSource);
        done(`updated option "${x.description}"`);
        break;
      }
      case "remove_option":
        if (!f.options.some((x) => x.id === o.option)) skip(`no option ${o.option}`);
        else {
          f.options = f.options.filter((x) => x.id !== o.option);
          done("removed an option");
        }
        break;
      case "add_unit_price": {
        const u: CustomUnitPrice = { id: newPartId(), description: o.description, unit: o.unit, unitPriceCents: cents(o.unitPrice), aiSource: aiOf(o.priceSource, null) };
        f.unitPrices.push(u);
        done(`added unit price "${o.description}" (id ${u.id})`);
        break;
      }
      case "update_unit_price": {
        const u = f.unitPrices.find((x) => x.id === o.unitPrice);
        if (!u) {
          skip(`no unit price ${o.unitPrice}`);
          break;
        }
        if (o.description !== undefined) u.description = o.description;
        if (o.unit !== undefined) u.unit = o.unit;
        if (o.price !== undefined) {
          u.unitPriceCents = cents(o.price);
          u.aiSource = aiOf(o.priceSource ?? "AI estimate (no source given)", u.aiSource);
        } else if (o.priceSource !== undefined) u.aiSource = aiOf(o.priceSource, u.aiSource);
        done(`updated unit price "${u.description}"`);
        break;
      }
      case "remove_unit_price":
        if (!f.unitPrices.some((x) => x.id === o.unitPrice)) skip(`no unit price ${o.unitPrice}`);
        else {
          f.unitPrices = f.unitPrices.filter((x) => x.id !== o.unitPrice);
          done("removed a unit price");
        }
        break;
      case "set_list":
        f[o.list] = o.list === "selections" ? o.items.slice(0, 10).map((s) => s.slice(0, 80)) : o.items;
        done(`set ${o.list}`);
        break;
      case "set_schedule":
        f.schedule = { start: o.start ?? f.schedule.start, duration: o.duration ?? f.schedule.duration, notes: o.notes ?? f.schedule.notes };
        done("set the schedule");
        break;
      case "set_payments": {
        const b = { ...f.billing };
        if (o.mode) b.mode = o.mode;
        if (o.milestones) {
          if (o.milestones.slice(0, -1).some((m) => m.percent === undefined && m.amount === undefined)) {
            skip("every payment but the last needs a percent or an amount");
            break;
          }
          b.milestones = o.milestones.map((m, i) => {
            const last = i === o.milestones!.length - 1;
            return { id: newPartId(), label: m.label, when: m.when ?? "", percentMilli: !last && m.percent !== undefined ? thousandths(m.percent) : null, amountCents: !last && m.percent === undefined && m.amount !== undefined ? cents(m.amount) : null };
          });
        }
        if (o.retainagePercent !== undefined) b.retainagePercentMilli = thousandths(o.retainagePercent);
        if (o.stepDown !== undefined) b.stepDown = o.stepDown ? { atCompletePercent: o.stepDown.atCompletePercent, toPercentMilli: thousandths(o.stepDown.toPercent) } : null;
        if (o.storedMaterials !== undefined) b.storedMaterials = o.storedMaterials;
        if (o.terms !== undefined) b.terms = o.terms;
        f.billing = b;
        done("set the payments");
        break;
      }
      case "set_show":
        f.show = { ...f.show, ...Object.fromEntries(Object.entries(o).filter(([k, v]) => k !== "op" && v !== undefined)) };
        done("set what the customer sees");
        break;
    }
  }
  const checked = customFormSchema.safeParse(f);
  if (!checked.success) return { form: start, log: [...log, `Nothing was saved: the result wasn't a valid proposal (${checked.error.issues[0]?.path.join(".")}: ${checked.error.issues[0]?.message}).`], changed: false };
  f = checked.data;
  return { form: f, log, changed: JSON.stringify(f) !== JSON.stringify(start) };
}

function item(i: z.infer<typeof itemIn>): CustomItem {
  return { id: newPartId(), description: i.description, quantity: thousandths(i.quantity), unit: i.unit, unitPriceCents: cents(i.unitPrice), unitCostCents: i.unitCost != null ? cents(i.unitCost) : null, aiSource: aiOf(i.priceSource, null) };
}

const d = (c: number) => c / 100;

/** The draft as the AI reads it: dollars, plain quantities and percents, with ids, and the CRM's totals. */
export function formForAi(f: CustomForm, minMarginPercent: number) {
  const t = customTotals(f, minMarginPercent);
  return {
    market: f.market,
    marketSetBy: f.marketSetBy,
    title: f.title,
    intro: f.intro,
    ...(f.market === "commercial" ? { cover: f.cover } : {}),
    sections: f.sections.map((s, i) => ({
      id: s.id,
      heading: s.heading,
      description: s.description,
      included: s.included.filter((x) => x.trim()),
      pricing: s.pricing,
      ...(s.pricing === "lump" ? { price: d(s.priceCents) } : {}),
      cost: s.costCents === null ? null : d(s.costCents),
      ...(s.pricing === "items" ? { items: s.items.map((x) => ({ id: x.id, description: x.description, quantity: x.quantity / 1000, unit: x.unit, unitPrice: d(x.unitPriceCents), unitCost: x.unitCostCents === null ? null : d(x.unitCostCents), ...(x.aiSource ? { aiEstimate: x.aiSource } : {}) })) } : {}),
      total: d(t.sections[i]!.priceCents),
      ...(s.aiSource ? { aiEstimate: s.aiSource } : {}),
    })),
    allowances: f.allowances.map((a, i) => ({ id: a.id, description: a.description, ...(a.quantity !== null && a.unitPriceCents !== null ? { quantity: a.quantity / 1000, unit: a.unit, unitPrice: d(a.unitPriceCents) } : {}), amount: d(t.allowances[i]!.amountCents), cost: a.costCents === null ? null : d(a.costCents), ...(a.aiSource ? { aiEstimate: a.aiSource } : {}) })),
    options: f.options.map((o, i) => ({ id: o.id, label: optionLabel(o, i, f.options), kind: o.kind, description: o.description, amount: d(o.amountCents), cost: o.costCents === null ? null : d(o.costCents), ...(o.aiSource ? { aiEstimate: o.aiSource } : {}) })),
    unitPrices: f.unitPrices.map((u) => ({ id: u.id, description: u.description, unit: u.unit, price: d(u.unitPriceCents), ...(u.aiSource ? { aiEstimate: u.aiSource } : {}) })),
    exclusions: f.exclusions.filter((x) => x.trim()),
    qualifications: f.qualifications.filter((x) => x.trim()),
    selections: f.selections,
    schedule: f.schedule,
    payments:
      f.billing.mode === "milestones"
        ? { mode: "milestones", milestones: t.payments.map((p) => ({ label: p.label, when: p.when, ...(p.rest ? { rest: true } : p.percentMilli !== null ? { percent: p.percentMilli / 1000 } : {}), amount: d(p.amountCents) })) }
        : { mode: "progress", retainagePercent: f.billing.retainagePercentMilli / 1000, stepDown: f.billing.stepDown ? { atCompletePercent: f.billing.stepDown.atCompletePercent, toPercent: f.billing.stepDown.toPercentMilli / 1000 } : null, storedMaterials: f.billing.storedMaterials, terms: f.billing.terms },
    discount: d(f.discountCents),
    taxPercent: f.taxPercentMilli / 1000,
    validDays: f.validDays,
    warranty: f.warranty,
    notes: f.notes,
    show: f.show,
    totals: { base: d(t.baseCents), tax: d(t.taxCents), contract: d(t.contractCents), cost: d(t.costCents), marginPercent: t.marginPercent, minMarginPercent, belowMinimum: t.belowMinimum, partsWithoutCost: t.missingCosts },
    problemsBeforeSending: t.problems,
  };
}

export interface CustomAiContext {
  /** Today, "October 28, 2026". */
  today: string;
  company: string;
  rep: string;
  job: { number: number; title: string; workflow: string; stage: string; address: string; notes: string[] };
  customer: { name: string; kind: "residential" | "commercial"; phone: string; email: string };
  files: { id: string; name: string; category: string; type: string; sizeKb: number }[];
  /** "Things Fred should know" (Settings → AI). */
  notes: string[];
  /** The General Conditions every proposal carries (titles and text). */
  generalConditions: { title?: string; body: string }[];
  draft: ReturnType<typeof formForAi>;
}

/** The builder's instructions: who it is, how it prices, and the job and draft as they are now. */
export function customProposalSystemPrompt(c: CustomAiContext): string {
  const files = c.files.length ? c.files.map((f) => `- ${f.name} (${f.category || "no category"}, ${f.type}, ${f.sizeKb} KB) id ${f.id}`).join("\n") : "(none)";
  return `You build proposals for ${c.company}, a roofing and construction company in Moore, Oklahoma, together with ${c.rep}, the rep. This is a custom proposal: remodels, repairs, commercial work, anything that isn't a priced roof or an insurance estimate. Today is ${c.today}.

How you work:
- Interview the rep. Ask only what's missing and matters, at most three questions at a time, and draft as you go: don't wait for every answer before putting something in the draft.
- You change the draft only with the edit_proposal tool. Nothing you say changes the proposal unless it's in an edit. After editing, say in a sentence or two what you changed; the rep sees the draft update beside the chat. Don't repeat the whole proposal back.
- Write for the customer: plain, confident, specific about what we'll do and what's included, in NMT's voice. Short paragraphs; "what's included" as short bullet lines. For commercial work use normal bid language (base bid, alternates, qualifications, exclusions, schedule of values) and fill in the project block.
- Keep it consistent with the General Conditions below; don't repeat them or contradict them (change orders, warranty, payment, delays).
- Say each point once, in the one place it belongs, and don't restate it elsewhere: the work and what's included in its section; prices for extra work in unit prices (the section can say "beyond that, at the unit price" once); what isn't included in exclusions; conditions and responsibilities in qualifications/clarifications; the timing in the schedule. The opening paragraph is one or two sentences of overview, not a repeat of the first section. Leave notes empty unless something fits nowhere else. When the rep asks you to tighten or clean up, remove repeats rather than reword them.
- A section with an empty heading is a blank placeholder from when the proposal was started: fill it in or remove it.
- Sections: one per part of the job, priced as a lump sum, by items (quantity × unit price), or "included" (part of the base price with no price of its own). Use allowances for things the customer picks later (tile, fixtures, lighting), options and deduct alternates for choices the customer makes when signing, and unit prices for extra work that may come up (rotten decking, extra drywall).
- Payments: residential usually milestones (a deposit at signing, payments as work is reached, the rest at completion). Keep money due before work starts at 50% or less unless the rep says otherwise, and say so if it's more. Commercial usually progress billing with retainage (5-10%) and Net 30.
- Residential or commercial: it starts from the customer record. If the rep set it (marketSetBy "rep"), never change it. Otherwise, switch it when the conversation makes it clear (a GC, a PO, retainage, pay applications, a business owner or tenant finish-out) and say that you did.

Prices (the rep's rules):
- Use the rep's numbers whenever given, with priceSource "rep".
- Suggest a price only when the rep asks, or when a price is needed and we have nothing to go on. Look first at our own data: past_proposals (what NMT charged for similar work), search_materials (ABC prices at NMT's branch), labor_price_list (NMT's labor prices and subs' rates), and any sub bids or plans in the job's files (read_file). For a sub bid, price it as the bid plus a markup and show the markup. Only then fall back to a general market estimate for central Oklahoma, and say it's a rough one.
- Every suggested price gets priceSource saying exactly where it came from ("NMT's 3 past bathroom remodels, $38-46k", "ABC price $412/sq x 1.1 waste + $120/sq labor", "sub bid $18,400 + 15% markup", "general market estimate for central Oklahoma"). The rep accepts each one before the proposal can go out; never mark your own estimate as "rep" unless the rep gave or confirmed that number in this conversation.
- Never do the arithmetic for the document: give quantities and unit prices, or lump sums, and the CRM adds them up. The totals and margin in the draft are the CRM's.
- Costs (what NMT pays) are for the margin only and never shown to the customer. Fill them in when you know them (catalog, labor list, sub bids). Warn when the margin is under the minimum.

Watch for and point out risks: work that needs a permit with none mentioned, an allowance with no quantity, who supplies what (owner-furnished items), access, disposal, hidden conditions (rot, asbestos in older homes, electrical or plumbing found behind walls), weather, lead times, and anything in the plans or sub bids that the scope doesn't cover. Suggest exclusions and clarifications that protect NMT.

You can't send, sign, email, or change anything outside this proposal. If the rep asks for that, tell them where to do it.

The job: #${c.job.number} ${c.job.title}, ${c.job.workflow} (${c.job.stage}). Address: ${c.job.address || "none"}.
Customer: ${c.customer.name} (${c.customer.kind})${c.customer.phone ? `, ${c.customer.phone}` : ""}${c.customer.email ? `, ${c.customer.email}` : ""}.
${c.job.notes.length ? `Job notes (newest first):\n${c.job.notes.map((n) => `- ${n}`).join("\n")}` : "No job notes."}

The job's files (read_file to look at a PDF or a photo):
${files}
${c.notes.length ? `\nThings NMT wants you to know:\n${c.notes.map((n) => `- ${n}`).join("\n")}\n` : ""}
General Conditions on every proposal (for consistency; don't copy them in):
${c.generalConditions.map((g) => `${g.title ? `${g.title}: ` : ""}${g.body}`).join("\n")}

The draft right now (dollars; ids are what edit_proposal refers to):
${JSON.stringify(c.draft)}`;
}

/** A turn's change, as the chat keeps it. */
export interface CustomChatEntry {
  role: "user" | "assistant";
  text: string;
  at: string;
  /** Assistant turns that changed the draft: the version saved from before, to undo to. */
  versionId?: string;
  /** What was read and changed, for the rep ("Read plans.pdf", "Added 3 sections"). */
  steps?: string[];
}

/** What the builder streams to the browser, one JSON object per line. */
export type CustomChatEvent =
  | { type: "text"; text: string }
  /** Something it did ("Read plans.pdf", "Changed the draft (4 changes)"); with the draft when it changed. */
  | { type: "step"; text: string; form?: CustomForm }
  | { type: "error"; message: string }
  | { type: "done" };
